/**
 * Frontmatter parsing and manipulation (platform-independent)
 */
import matter from 'gray-matter';
import yaml from 'js-yaml';
import type { SpecFrontmatter, SpecStatus, SpecPriority, StatusTransition, SpecFilterOptions, LeanSpecConfig } from '../types/index.js';

/**
 * Convert Date objects to YYYY-MM-DD string format
 * (gray-matter auto-parses YYYY-MM-DD strings as Date objects)
 */
export function normalizeDateFields(data: Record<string, unknown>): void {
  const dateFields = ['created', 'completed', 'updated', 'due'];
  
  for (const field of dateFields) {
    if (data[field] instanceof Date) {
      data[field] = (data[field] as Date).toISOString().split('T')[0];
    }
  }
}

/**
 * Enrich frontmatter with timestamps for velocity tracking
 * Auto-generates timestamps when missing and tracks status transitions
 */
export function enrichWithTimestamps(
  data: Record<string, unknown>,
  previousData?: Record<string, unknown>
): void {
  const now = new Date().toISOString();

  // Set created_at if missing - always use current timestamp
  // Do NOT infer from created date field since that's just YYYY-MM-DD without time
  if (!data.created_at) {
    data.created_at = now;
  }

  // Update updated_at on any change (if previousData exists)
  if (previousData) {
    data.updated_at = now;
  }

  // Set completed_at when status changes to complete
  if (
    data.status === 'complete' &&
    previousData?.status !== 'complete' &&
    !data.completed_at
  ) {
    data.completed_at = now;
    // Also set the completed date field
    if (!data.completed) {
      data.completed = new Date().toISOString().split('T')[0];
    }
  }

  // Track status transition (optional)
  if (previousData && data.status !== previousData.status) {
    if (!Array.isArray(data.transitions)) {
      data.transitions = [];
    }
    (data.transitions as StatusTransition[]).push({
      status: data.status as SpecStatus,
      at: now,
    });
  }
}

/**
 * Normalize tags field - parse JSON strings into arrays
 * Handles cases where AI accidentally creates tags as '["..",".."]' strings
 */
export function normalizeTagsField(data: Record<string, unknown>): void {
  if (data.tags && typeof data.tags === 'string') {
    try {
      // Try to parse as JSON array
      const parsed = JSON.parse(data.tags as string);
      if (Array.isArray(parsed)) {
        data.tags = parsed;
      }
    } catch {
      // If not valid JSON, treat as comma-separated string
      data.tags = (data.tags as string).split(',').map(t => t.trim());
    }
  }
}

/**
 * Validate and coerce custom field types
 */
export function validateCustomField(
  value: unknown,
  expectedType: 'string' | 'number' | 'boolean' | 'array'
): { valid: boolean; coerced?: unknown; error?: string } {
  switch (expectedType) {
    case 'string':
      if (typeof value === 'string') {
        return { valid: true, coerced: value };
      }
      // Coerce to string
      return { valid: true, coerced: String(value) };
    
    case 'number':
      if (typeof value === 'number') {
        return { valid: true, coerced: value };
      }
      // Try to coerce to number
      const num = Number(value);
      if (!isNaN(num)) {
        return { valid: true, coerced: num };
      }
      return { valid: false, error: `Cannot convert '${value}' to number` };
    
    case 'boolean':
      if (typeof value === 'boolean') {
        return { valid: true, coerced: value };
      }
      // Coerce string to boolean
      if (value === 'true' || value === 'yes' || value === '1') {
        return { valid: true, coerced: true };
      }
      if (value === 'false' || value === 'no' || value === '0') {
        return { valid: true, coerced: false };
      }
      return { valid: false, error: `Cannot convert '${value}' to boolean` };
    
    case 'array':
      if (Array.isArray(value)) {
        return { valid: true, coerced: value };
      }
      return { valid: false, error: `Expected array but got ${typeof value}` };
    
    default:
      return { valid: false, error: `Unknown type: ${expectedType}` };
  }
}

/**
 * Validate custom fields according to config
 */
export function validateCustomFields(
  frontmatter: Record<string, unknown>,
  config?: LeanSpecConfig
): Record<string, unknown> {
  if (!config?.frontmatter?.custom) {
    return frontmatter;
  }
  
  const result = { ...frontmatter };
  
  for (const [fieldName, expectedType] of Object.entries(config.frontmatter.custom)) {
    if (fieldName in result) {
      const validation = validateCustomField(result[fieldName], expectedType);
      if (validation.valid) {
        result[fieldName] = validation.coerced;
      } else {
        console.warn(`Warning: Invalid custom field '${fieldName}': ${validation.error}`);
      }
    }
  }
  
  return result;
}

/**
 * Parse frontmatter from markdown content string
 */
export function parseFrontmatterFromString(
  content: string,
  filePath?: string,
  config?: LeanSpecConfig
): SpecFrontmatter | null {
  try {
    const parsed = matter(content, {
      engines: {
        yaml: (str) => yaml.load(str, { schema: yaml.FAILSAFE_SCHEMA }) as Record<string, unknown>
      }
    });

    if (!parsed.data || Object.keys(parsed.data).length === 0) {
      // No frontmatter found, try fallback to inline fields
      return parseFallbackFields(content);
    }

    // Validate required fields
    if (!parsed.data.status) {
      if (filePath) console.warn(`Warning: Missing required field 'status' in ${filePath}`);
      return null;
    }

    if (!parsed.data.created) {
      if (filePath) console.warn(`Warning: Missing required field 'created' in ${filePath}`);
      return null;
    }

    // Validate status enum
    const validStatuses: SpecStatus[] = ['planned', 'in-progress', 'complete', 'archived'];
    if (!validStatuses.includes(parsed.data.status)) {
      if (filePath) {
        console.warn(`Warning: Invalid status '${parsed.data.status}' in ${filePath}. Valid values: ${validStatuses.join(', ')}`);
      }
    }

    // Validate priority enum if present
    if (parsed.data.priority) {
      const validPriorities: SpecPriority[] = ['low', 'medium', 'high', 'critical'];
      if (!validPriorities.includes(parsed.data.priority)) {
        if (filePath) {
          console.warn(`Warning: Invalid priority '${parsed.data.priority}' in ${filePath}. Valid values: ${validPriorities.join(', ')}`);
        }
      }
    }

    // Normalize tags field (parse JSON strings to arrays)
    normalizeTagsField(parsed.data);
    
    // Warn about unknown fields (informational only)
    const knownFields = [
      'status', 'created', 'tags', 'priority', 'related', 'depends_on',
      'updated', 'completed', 'assignee', 'reviewer', 'issue', 'pr', 'epic', 'breaking', 'due',
      'created_at', 'updated_at', 'completed_at', 'transitions'
    ];
    
    // Add custom fields from config to known fields
    const customFields = config?.frontmatter?.custom ? Object.keys(config.frontmatter.custom) : [];
    const allKnownFields = [...knownFields, ...customFields];
    
    const unknownFields = Object.keys(parsed.data).filter(k => !allKnownFields.includes(k));
    if (unknownFields.length > 0 && filePath) {
      console.warn(`Info: Unknown fields in ${filePath}: ${unknownFields.join(', ')}`);
    }
    
    // Validate and coerce custom fields
    const validatedData = validateCustomFields(parsed.data, config);

    return validatedData as SpecFrontmatter;
  } catch (error) {
    console.error(`Error parsing frontmatter${filePath ? ` from ${filePath}` : ''}:`, error);
    return null;
  }
}

/**
 * Fallback: Parse inline fields from older specs
 */
function parseFallbackFields(content: string): SpecFrontmatter | null {
  const statusMatch = content.match(/\*\*Status\*\*:\s*(?:📅\s*)?(\w+(?:-\w+)?)/i);
  const createdMatch = content.match(/\*\*Created\*\*:\s*(\d{4}-\d{2}-\d{2})/);

  if (statusMatch && createdMatch) {
    const status = statusMatch[1].toLowerCase().replace(/\s+/g, '-') as SpecStatus;
    const created = createdMatch[1];

    return {
      status,
      created,
    };
  }

  return null;
}

/**
 * Create updated frontmatter with changes
 */
export function createUpdatedFrontmatter(
  existingContent: string,
  updates: Partial<SpecFrontmatter>
): { content: string; frontmatter: SpecFrontmatter } {
  const parsed = matter(existingContent, {
    engines: {
      yaml: (str) => yaml.load(str, { schema: yaml.FAILSAFE_SCHEMA }) as Record<string, unknown>
    }
  });

  // Store previous data for timestamp enrichment
  const previousData = { ...parsed.data };

  // Merge updates with existing data
  const newData = { ...parsed.data, ...updates };

  // Ensure date fields remain as strings (gray-matter auto-parses YYYY-MM-DD as Date objects)
  normalizeDateFields(newData);

  // Enrich with timestamps
  enrichWithTimestamps(newData, previousData);

  // Auto-update timestamps if fields exist (legacy behavior)
  if (updates.status === 'complete' && !newData.completed) {
    newData.completed = new Date().toISOString().split('T')[0];
  }

  if ('updated' in parsed.data) {
    newData.updated = new Date().toISOString().split('T')[0];
  }

  // Update visual metadata badges in content
  let updatedContent = parsed.content;
  updatedContent = updateVisualMetadata(updatedContent, newData as SpecFrontmatter);

  // Stringify back to markdown
  const newContent = matter.stringify(updatedContent, newData);
  
  return {
    content: newContent,
    frontmatter: newData as SpecFrontmatter
  };
}

/**
 * Update visual metadata badges in content
 */
function updateVisualMetadata(content: string, frontmatter: SpecFrontmatter): string {
  const statusEmoji = getStatusEmojiPlain(frontmatter.status);
  const statusLabel = frontmatter.status.charAt(0).toUpperCase() + frontmatter.status.slice(1).replace('-', ' ');
  
  // Use the created date as-is (already in YYYY-MM-DD format)
  const created = frontmatter.created;
  
  // Build metadata line
  let metadataLine = `> **Status**: ${statusEmoji} ${statusLabel}`;
  
  if (frontmatter.priority) {
    const priorityLabel = frontmatter.priority.charAt(0).toUpperCase() + frontmatter.priority.slice(1);
    metadataLine += ` · **Priority**: ${priorityLabel}`;
  }
  
  metadataLine += ` · **Created**: ${created}`;
  
  if (frontmatter.tags && frontmatter.tags.length > 0) {
    metadataLine += ` · **Tags**: ${frontmatter.tags.join(', ')}`;
  }
  
  // For enterprise template with assignee/reviewer
  let secondLine = '';
  if (frontmatter.assignee || frontmatter.reviewer) {
    const assignee = frontmatter.assignee || 'TBD';
    const reviewer = frontmatter.reviewer || 'TBD';
    secondLine = `\n> **Assignee**: ${assignee} · **Reviewer**: ${reviewer}`;
  }
  
  // Replace existing metadata block or add after title
  const metadataPattern = /^>\s+\*\*Status\*\*:.*(?:\n>\s+\*\*Assignee\*\*:.*)?/m;
  
  if (metadataPattern.test(content)) {
    // Replace existing metadata
    return content.replace(metadataPattern, metadataLine + secondLine);
  } else {
    // Add after title (# title)
    const titleMatch = content.match(/^#\s+.+$/m);
    if (titleMatch) {
      const insertPos = titleMatch.index! + titleMatch[0].length;
      return content.slice(0, insertPos) + '\n\n' + metadataLine + secondLine + '\n' + content.slice(insertPos);
    }
  }
  
  return content;
}

function getStatusEmojiPlain(status: string): string {
  switch (status) {
    case 'planned': return '📅';
    case 'in-progress': return '⏳';
    case 'complete': return '✅';
    case 'archived': return '📦';
    default: return '📄';
  }
}

/**
 * Filter specs by criteria
 */
export function matchesFilter(frontmatter: SpecFrontmatter, filter: SpecFilterOptions): boolean {
  // Status filter
  if (filter.status) {
    const statuses = Array.isArray(filter.status) ? filter.status : [filter.status];
    if (!statuses.includes(frontmatter.status)) {
      return false;
    }
  }

  // Tags filter (spec must have ALL specified tags)
  if (filter.tags && filter.tags.length > 0) {
    if (!frontmatter.tags || frontmatter.tags.length === 0) {
      return false;
    }
    const hasAllTags = filter.tags.every(tag => frontmatter.tags!.includes(tag));
    if (!hasAllTags) {
      return false;
    }
  }

  // Priority filter
  if (filter.priority) {
    const priorities = Array.isArray(filter.priority) ? filter.priority : [filter.priority];
    if (!frontmatter.priority || !priorities.includes(frontmatter.priority)) {
      return false;
    }
  }

  // Assignee filter
  if (filter.assignee) {
    if (frontmatter.assignee !== filter.assignee) {
      return false;
    }
  }

  return true;
}
